import type { VoucherCodeResponse } from '@/api/model/voucherCodeResponse';
import { VoucherStatus } from '@/api/model/voucherStatus';
import { useListVoucherCodes, useReleaseSingleCode, useUseSingleCode } from '@/api/voucher-codes/voucher-codes';
import {
  useArchiveVoucher,
  useGenerateVoucherCodes,
  useGetVoucher,
  useImportVoucherCodes,
  usePublishVoucher,
} from '@/api/vouchers/vouchers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { VoucherCodesModal } from '@/components/vouchers/VoucherCodesModal';
import { VoucherEditForm } from '@/components/vouchers/VoucherEditForm';
import type { VoucherFormData } from '@/components/vouchers/VoucherForm';
import { VoucherPreview } from '@/components/vouchers/VoucherPreview';
import { ImageUrlsField } from '@/components/ui/image-urls-field';
import { getApiErrorMessage } from '@/lib/api-error';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { Archive, ArrowLeft, Plus, Send } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

export const VoucherDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [previewData, setPreviewData] = useState<VoucherFormData | null>(null);
  const [formInstance, setFormInstance] = useState<UseFormReturn<VoucherFormData> | null>(null);
  const [showCodes, setShowCodes] = useState(false);
  const [page, setPage] = useState(1);
  const [accumulatedCodes, setAccumulatedCodes] = useState<VoucherCodeResponse[]>([]);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateCount, setGenerateCount] = useState(100);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [codesCsv, setCodesCsv] = useState('');
  const { isAdmin } = useAuth();

  const {
    data: voucher,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetVoucher(id ?? '', {
    query: {
      enabled: !!id,
    },
  });

  // Available codes count for header display
  const availableCodesQuery = useListVoucherCodes(
    { voucher_id: id ?? '', page: 1, per_page: 1, only_available: true },
    { query: { enabled: !!id } }
  );

  const archiveMutation = useArchiveVoucher();

  const publishMutation = usePublishVoucher();

  const generateCodesMutation = useGenerateVoucherCodes();
  const importCodesMutation = useImportVoucherCodes();

  const codesQuery = useListVoucherCodes(
    { voucher_id: id ?? '', page, per_page: 20, only_available: false },
    { query: { enabled: !!id && showCodes } }
  );

  // Reset pagination when opening modal
  useEffect(() => {
    if (showCodes && id) {
      setPage(1);
      // Don't clear accumulatedCodes here - keep previous data visible when reopening
      // This prevents "no data" flash. The query will automatically fetch when enabled
      // and update accumulatedCodes through the effect below
    }
    // Don't clear accumulatedCodes when closing - only clear when voucher changes
    // This ensures data is available immediately when reopening the modal
  }, [showCodes, id]);

  // Clear accumulated codes when voucher changes
  useEffect(() => {
    setAccumulatedCodes([]);
    setPage(1);
  }, [voucher?.id]);

  // Accumulate codes across pages, deduplicating by id
  useEffect(() => {
    const pageData = codesQuery.data;
    if (!pageData) return;

    if (page === 1) {
      // Always set accumulated codes for page 1, even if it's the same data
      // This ensures we don't show "no data" when modal opens with cached data
      setAccumulatedCodes(pageData.items);
    } else {
      setAccumulatedCodes(prev => {
        const prevIdSet = new Set(prev.map(c => c.id));
        const replaced = prev.map(c => {
          const updated = pageData.items.find(i => i.id === c.id);
          return updated ?? c;
        });
        const newItems = pageData.items.filter(i => !prevIdSet.has(i.id));
        return [...replaced, ...newItems];
      });
    }
  }, [codesQuery.data, page]);

  const hasMore = Boolean(codesQuery.data && codesQuery.data.page < codesQuery.data.pages);
  const isLoadingMore = codesQuery.isFetching && page > 1;
  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      setPage(p => p + 1);
    }
  };

  const releaseSingle = useReleaseSingleCode();
  const markUsed = useUseSingleCode();

  if (isLoading) {
    return (
      <div className='container mx-auto p-6'>
        <div className='text-center'>Gutscheindetails werden geladen...</div>
      </div>
    );
  }

  if (isError || !voucher) {
    return (
      <div className='container mx-auto p-6'>
        <Card>
          <CardContent className='pt-6'>
            <div className='text-center text-red-500'>
              {error instanceof Error
                ? `Fehler beim Laden des Gutscheins: ${error.message}`
                : 'Gutschein nicht gefunden'}
            </div>
            <div className='flex justify-center mt-4'>
              <Button onClick={() => navigate('/admin/vouchers')}>Zurück zu Gutscheinen</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleEditSuccess = () => {
    refetch();
  };

  const handleCancelEdit = () => {
    navigate('/admin/vouchers');
  };

  const handleArchive = () => {
    if (voucher?.id) {
      archiveMutation
        .mutateAsync({ voucherId: voucher.id })
        .then(() => {
          toast.success('Gutschein erfolgreich archiviert');
          refetch();
        })
        .catch(error => {
          toast.error(getApiErrorMessage(error, 'Archivieren des Gutscheins fehlgeschlagen'));
        });
    }
  };

  const handlePublish = () => {
    if (voucher?.id) {
      publishMutation
        .mutateAsync({ voucherId: voucher.id })
        .then(() => {
          toast.success('Gutschein erfolgreich veröffentlicht');
          refetch();
        })
        .catch(error => {
          toast.error(getApiErrorMessage(error, 'Veröffentlichen des Gutscheins fehlgeschlagen'));
        });
    }
  };

  const handleGenerateCodes = () => {
    if (voucher?.id) {
      generateCodesMutation
        .mutateAsync({
          voucherId: voucher.id,
          data: { count: generateCount },
        })
        .then(response => {
          toast.success(response.message);
          setShowGenerateDialog(false);
          availableCodesQuery.refetch();
          if (showCodes) {
            codesQuery.refetch();
          }
        })
        .catch(error => {
          toast.error(getApiErrorMessage(error, 'Generieren der Codes fehlgeschlagen'));
        });
    }
  };

  if (!voucher) {
    return null;
  }

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-4'>
          <Link to='/admin/vouchers' className='flex items-center gap-2'>
            <ArrowLeft className='h-6 w-6 text-swd-tuerkis-dark' />
          </Link>
          <h1 className='text-3xl leading-9 font-semibold text-swd-tuerkis-darker'>Gutschein bearbeiten</h1>
          <div className='flex gap-2 ml-auto'>
            {isAdmin && (
              <>
                <Button
                  variant='outline'
                  className='h-[42px] rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-sm font-medium tracking-wide px-4 hover:bg-neutral-hellgrau/50'
                  onClick={() => setShowCodes(true)}
                >
                  Codes verwalten
                </Button>
                <Button
                  variant='outline'
                  className='h-[42px] rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-sm font-medium tracking-wide px-4 hover:bg-neutral-hellgrau/50'
                  onClick={() => setShowGenerateDialog(true)}
                  disabled={voucher.status === VoucherStatus.ARCHIVED}
                >
                  <Plus className='h-4 w-4 mr-2' />
                  Codes generieren
                </Button>
                <Button
                  variant='outline'
                  className='h-[42px] rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-sm font-medium tracking-wide px-4 hover:bg-neutral-hellgrau/50'
                  onClick={() => setShowImportDialog(true)}
                  disabled={voucher.status === VoucherStatus.ARCHIVED}
                >
                  <Plus className='h-4 w-4 mr-2' />
                  Codes importieren
                </Button>
              </>
            )}
            {voucher.status === VoucherStatus.DRAFT && (
              <Button
                onClick={handlePublish}
                disabled={publishMutation.isPending}
                variant='outline'
                className='h-[42px] rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-sm font-medium tracking-wide px-4 hover:bg-neutral-hellgrau/50'
              >
                <Send className='h-4 w-4 mr-2' />
                {publishMutation.isPending ? 'Veröffentlichen...' : 'Veröffentlichen'}
              </Button>
            )}

            {voucher.status !== VoucherStatus.ARCHIVED && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant='outline'
                    className='h-[42px] rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-sm font-medium tracking-wide px-4 hover:bg-neutral-hellgrau/50'
                    disabled={archiveMutation.isPending}
                  >
                    <Archive className='h-4 w-4 mr-2' />
                    Archivieren
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Gutschein archivieren</AlertDialogTitle>
                    <AlertDialogDescription>
                      Sind Sie sicher, dass Sie diesen Gutschein archivieren möchten? Diese Aktion blendet ihn aus der
                      Liste aus und verhindert weitere Änderungen. Archivierte Gutscheine können nicht mehr von
                      Kundinnen und Kunden genutzt werden.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleArchive}
                      disabled={archiveMutation.isPending}
                      className='bg-red-600 hover:bg-red-700'
                    >
                      {archiveMutation.isPending ? 'Wird archiviert...' : 'Gutschein archivieren'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
        <p className='text-base leading-6 font-medium tracking-wide text-neutral-grau'>
          Aktualisieren Sie die Gutscheininformationen
        </p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Form Section */}
        <div className='lg:col-span-2'>
          <VoucherEditForm
            voucher={voucher}
            onSuccess={handleEditSuccess}
            onCancel={handleCancelEdit}
            onFormDataChange={setPreviewData}
            hideImagesField
            onFormInstance={setFormInstance}
          />
        </div>

        {/* Preview Section */}
        <div className='lg:sticky lg:top-6 lg:self-start lg:col-span-1 space-y-6'>
          {previewData && <VoucherPreview formData={previewData} />}
          {formInstance && (
            <>
              <Form {...formInstance}>
                <ImageUrlsField
                  control={formInstance.control}
                  name={'images'}
                  label='Bild'
                  description='Bild-URLs für diesen Gutschein hinzufügen'
                  maxImages={5}
                />
              </Form>
              <Form {...formInstance}>
                <Card className='bg-white border border-neutral-hellgrau rounded-xl'>
                  <CardHeader>
                    <CardTitle>Status</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <FormField
                      control={formInstance.control}
                      name='status'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className='text-sm font-bold tracking-wide text-neutral-dunkelgrau'>
                            Status
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className='h-9 rounded-xl border-[1.5px] border-neutral-mittelgrau bg-white text-neutral-dunkelgrau text-sm font-medium w-full'>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={VoucherStatus.DRAFT}>Entwurf</SelectItem>
                              <SelectItem value={VoucherStatus.PUBLISHED}>Veröffentlicht</SelectItem>
                              <SelectItem value={VoucherStatus.ARCHIVED}>Archiviert</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </Form>
            </>
          )}
        </div>
      </div>

      <VoucherCodesModal
        open={showCodes}
        onOpenChange={open => setShowCodes(open)}
        voucherTitle={voucher.title}
        codes={accumulatedCodes}
        isLoading={page === 1 && accumulatedCodes.length === 0 && (codesQuery.isLoading || codesQuery.isFetching)}
        onRelease={codeId => {
          releaseSingle
            .mutateAsync({ codeId })
            .then(() => {
              toast.success('Code freigegeben');
              availableCodesQuery.refetch();
              codesQuery.refetch();
            })
            .catch(error => toast.error(getApiErrorMessage(error, 'Freigabe des Codes fehlgeschlagen')));
        }}
        onUse={codeId => {
          markUsed
            .mutateAsync({ codeId })
            .then(() => {
              toast.success('Code als verwendet markiert');
              availableCodesQuery.refetch();
              codesQuery.refetch();
            })
            .catch(error => toast.error(getApiErrorMessage(error, 'Markieren des Codes als verwendet fehlgeschlagen')));
        }}
        hasMore={hasMore}
        onLoadMore={loadMore}
        isLoadingMore={isLoadingMore}
        voucherValidUntil={voucher.valid_until}
      />

      {/* Generate Codes Dialog */}
      <AlertDialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gutscheincodes generieren</AlertDialogTitle>
            <AlertDialogDescription>
              Zusätzliche Codes für diesen Gutschein erzeugen. Sie können zwischen 1 und 1000 Codes auf einmal erzeugen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='py-4'>
            <Label htmlFor='generate-count'>Anzahl der zu generierenden Codes</Label>
            <Input
              id='generate-count'
              type='number'
              min={1}
              max={1000}
              value={generateCount}
              onChange={e => setGenerateCount(Math.max(1, Math.min(1000, Number(e.target.value))))}
              className='mt-2'
            />
            <p className='text-sm text-muted-foreground mt-2'>Standard: 100 Codes</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleGenerateCodes} disabled={generateCodesMutation.isPending}>
              {generateCodesMutation.isPending ? 'Wird generiert...' : ` ${generateCount} Codes generieren`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Codes Dialog */}
      <AlertDialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gutscheincodes importieren</AlertDialogTitle>
            <AlertDialogDescription>
              Fügen Sie eine kommagetrennte Liste von Codes ein. Maximal 1000 Codes pro Import. Jeder Code max. 256
              Zeichen. Leerzeichen an den Rändern werden entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='py-4'>
            <Label htmlFor='codes-csv'>Codes (CSV)</Label>
            <Textarea
              id='codes-csv'
              value={codesCsv}
              onChange={e => setCodesCsv(e.target.value)}
              className='mt-2'
              placeholder='CODE1,CODE2,CODE3'
            />
            <div className='text-xs text-muted-foreground mt-2'>
              Zeichen: {codesCsv.length.toLocaleString()} / 300.000
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!voucher?.id) return;
                if (codesCsv.length > 300000) {
                  toast.error('Maximale Zeichenanzahl (300.000) überschritten.');
                  return;
                }
                importCodesMutation
                  .mutateAsync({
                    voucherId: voucher.id,
                    data: { codes_csv: codesCsv },
                  })
                  .then(res => {
                    const success = res.success_count ?? 0;
                    const failed = res.failed_count ?? 0;
                    if (failed > 0) {
                      const firstFive = (res.errors ?? [])
                        .slice(0, 5)
                        .map(e => `${e.code ?? ''}: ${e.reason ?? ''}`)
                        .join('\n');
                      toast.error(`Import fehlgeschlagen: ${failed} Fehler.\n${firstFive}`);
                    } else {
                      toast.success(`${success} Codes erfolgreich importiert`);
                      setShowImportDialog(false);
                      setCodesCsv('');
                      availableCodesQuery.refetch();
                      if (showCodes) {
                        codesQuery.refetch();
                      }
                    }
                  })
                  .catch(err => {
                    toast.error(getApiErrorMessage(err, 'Import der Codes fehlgeschlagen'));
                  });
              }}
              disabled={importCodesMutation.isPending}
            >
              {importCodesMutation.isPending ? 'Wird importiert…' : 'Importieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
